/**
 * ScanSummaryPage — 스캔한 카드 요약 (SC-011 · FN-027 · FN-028).
 *
 * design 브랜치 산(마스터-디테일 배치)을 실 데이터로 이식 (2026-08-24):
 *   위    전체 스캔 카드 수 · 전체 시세 (전폭)
 *   좌    스캔한 카드 목록 — 고르기 + 제거(×). 선택된 항목은 액센트 테두리
 *   우    선택된 카드 — 큰 이미지 · 이름/번호 · 평가 · 수량 · 유형 · 등급
 *   아래  컬렉션에 담기 (전폭)
 *
 * FN-027 축 중 실데이터 현황:
 *   유형(FINISH)  카드 상세 variantPrices 에서 — 있으면 선택 가능, 값은 그 시세
 *   등급(Grading) **시세 미적재** — 자리만 두고 준비 중으로 밝힌다
 *   수량          단순 곱
 *
 * 담기(FN-028)는 카드마다 createHolding 을 돌린다 — 구매가는 §6.2 대로 선택
 * 입력이라 비워 담고, 담은 뒤 카드별로 넣게 안내한다(여러 장에 같은 값을 씌우면
 * 손익이 틀어진다 — CLAUDE.md 손익 규칙).
 *
 * ⚠ 여기는 카메라 위가 아니라 **테마 표면**이라 Astryx 를 그대로 쓴다.
 */
import { useEffect, useMemo, useState } from "react";
import { QTY_MAX, clampQty } from "../../components/collection/collectionLimits";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { Text } from "@astryxdesign/core/Text";
import { ToggleButton } from "@astryxdesign/core/ToggleButton";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import ConfirmDialog from "../../components/feedback/ConfirmDialog";
import Icon from "../../components/primitives/Icon";
import MisreadReportSheet from "../../components/report/MisreadReportSheet";
import { useMisreadReport } from "../../components/report/useMisreadReport";
import CardArt from "../../components/card/CardArt";
import { cardImageUrl, fetchCardDetail } from "../../api/card";
import { createHolding } from "../../api/collection";
import { formatKrw } from "../../lib/formatPrice";
import { maskPrice } from "../../lib/priceMasking";
import { useAuthStore } from "../../store/authStore";
import { useRequireAuth } from "../../navigation/useRequireAuth";
import { useActivityNavigator } from "../../navigation/useActivityNavigator";
import { nativeWarn } from "../../native";
import {
  scanTotals,
  scanUnitAmount,
  useScanSessionStore,
} from "../../store/scanSessionStore";
import { track } from "../../lib/tracking";
import styles from "./ScanSummaryPage.module.css";

export default function ScanSummaryPage() {
  const { t } = useTranslation("card");
  const items = useScanSessionStore((s) => s.items);
  const updateItem = useScanSessionStore((s) => s.updateItem);
  const removeItem = useScanSessionStore((s) => s.removeItem);
  const clear = useScanSessionStore((s) => s.clear);
  const isAuthenticated = useAuthStore((s) => !!s.accessToken);
  const { requireAuth } = useRequireAuth();
  const activityNav = useActivityNavigator();

  const totals = useMemo(() => scanTotals(items), [items]);

  // 요약 진입 1회 — 확정한 카드 수가 담기 전환의 분모다. items 는 편집으로 줄 수 있어
  // **진입 시점 값**을 쓴다(마운트 1회, 이후 변화는 담기 이벤트가 말한다).
  useEffect(() => {
    track("scan_summary_view", { cardCount: useScanSessionStore.getState().items.length });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** 우측에서 편집 중인 카드 — 처음엔 맨 위(=방금 잡은 것) */
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = items.find((it) => it.id === selectedId) ?? items[0] ?? null;

  // 편집 중이던 카드를 지우면 남은 것 중 맨 위로 (지운 id 를 계속 들고 있으면
  // 같은 id 가 다시 생길 때 엉뚱한 카드가 열린다 — design 주석 그대로)
  useEffect(() => {
    if (selectedId && !items.some((it) => it.id === selectedId)) {
      setSelectedId(items[0]?.id ?? null);
    }
  }, [items, selectedId]);

  // 유형(FINISH) 선택지 — 선택된 카드의 상세(variantPrices)에서. 실패해도 요약은
  // 동작해야 하므로 조용히 숨긴다(기본판 시세로 진행).
  const detailQuery = useQuery({
    queryKey: ["scan-summary-detail", selected?.card.cardSerialNo],
    queryFn: () => fetchCardDetail(selected!.card.cardSerialNo),
    enabled: selected !== null,
    staleTime: 5 * 60 * 1000,
  });
  const finishOptions = useMemo(() => {
    const variants = detailQuery.data?.variantPrices ?? [];
    const seen = new Map<string, number | null>();
    for (const v of variants) {
      if (!seen.has(v.finish)) seen.set(v.finish, v.price.amount);
    }
    return [...seen.entries()].map(([finish, amount]) => ({ finish, amount }));
  }, [detailQuery.data]);

  /* SC-027 — 잘못 인식된 카드를 여기서 바로 알린다. 비회원이면 훅이 로그인으로 보낸다.
     ⚠ 담기 전이라 '보유에서 빼기' 는 넘기지 않는다 — 뺄 보유가 아직 없다.
       여기서 잘못 잡힌 카드는 왼쪽 목록의 ×로 뺀다. */
  const { reportingUid, openReport, closeReport } = useMisreadReport();
  const reportingCard = reportingUid
    ? items.find((it) => it.card.cardUid === reportingUid)?.card ?? null
    : null;

  const [saveOpen, setSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    // 순차 저장이라 중간에 실패하면 앞쪽은 이미 담겨 있다 — 부분 저장이 실제 실패 모드라
    // savedCount 를 따로 센다(성공 경로에서는 cardCount 와 같다).
    let savedCount = 0;
    const cardCount = items.length;
    try {
      for (const it of items) {
        await createHolding({
          cardSerialNo: it.card.cardSerialNo,
          quantity: it.qty,
          finish: it.finish,
        });
        savedCount += 1;
      }
      // 일괄 담기는 여기서만 센다 — 카드별 collection_holding_added 는 단건 저장 폼
      // (HoldingFormPage) 전용이다. 둘 다 쏘면 담기 수가 두 배로 보인다.
      track("scan_summary_saved", { cardCount, savedCount });
      clear();
      setSaveOpen(false);
      activityNav.close();
    } catch (err) {
      track("scan_summary_saved", { cardCount, savedCount });
      nativeWarn("scanSummary:save", err);
      setNotice(t("scanSummary.saveFailed"));
      setSaveOpen(false);
    } finally {
      setSaving(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={t("scanSummary.emptyTitle")}
          description={t("scanSummary.emptyBody")}
        />
        <Button
          variant="secondary"
          label={t("scanSummary.backToScan")}
          clickAction={() => activityNav.close()}
        />
      </div>
    );
  }

  const price = (amount: number | null): string =>
    amount === null
      ? t("scanSummary.noPrice")
      : isAuthenticated
        ? formatKrw(amount)
        : maskPrice(amount);

  return (
    <div className={styles.page}>
      {/* 상단 — 전체 장수·합계 (전폭) */}
      <div className={styles.totals}>
        <Text as="p" type="supporting" color="secondary">
          {t("scanSummary.count", { count: totals.cardCount })}
        </Text>
        <p className={styles.totalAmt}>{price(totals.totalAmt)}</p>
        {totals.unknownCount > 0 && (
          <Text as="p" type="supporting" color="secondary">
            {t("scanSummary.unknown", { count: totals.unknownCount })}
          </Text>
        )}
      </div>

      <div className={styles.body}>
        {/* 좌측 레일 — 카드 고르기 + 제거 */}
        <ul className={styles.rail}>
          {items.map((it) => (
            <li key={it.id} className={styles.railItem}>
              <button
                type="button"
                className={`${styles.railCard} ${
                  selected?.id === it.id ? styles.railCardSelected : ""
                }`}
                onClick={() => setSelectedId(it.id)}
                aria-pressed={selected?.id === it.id}
              >
                <CardArt
                  className={styles.railThumb}
                  imgUrl={cardImageUrl(it.card.imagePathway) ?? undefined}
                  ariaLabel={it.card.name}
                />
                {it.qty > 1 && <span className={styles.railQty}>×{it.qty}</span>}
              </button>
              <button
                type="button"
                className={styles.railRemove}
                onClick={() => removeItem(it.id)}
                aria-label={t("scanSummary.remove", { name: it.card.name })}
              >
                ✕
              </button>
            </li>
          ))}
        </ul>

        {/* 우측 — 선택된 카드 편집 */}
        {selected && (
          <div className={styles.detail}>
            <CardArt
              className={styles.detailArt}
              imgUrl={cardImageUrl(selected.card.imagePathway) ?? undefined}
            />
            <VStack gap={2}>
              <Text as="p" weight="semibold">
                {selected.card.name}
              </Text>
              <Text as="p" type="supporting" color="secondary">
                {[selected.card.number, selected.card.language.toUpperCase()]
                  .filter(Boolean)
                  .join(" · ")}
              </Text>

              <Text as="p" type="supporting" color="secondary">
                {t("scanSummary.amount")} ·{" "}
                {price(
                  scanUnitAmount(selected) === null
                    ? null
                    : scanUnitAmount(selected)! * selected.qty,
                )}
                {selected.qty > 1 && scanUnitAmount(selected) !== null && (
                  <> ({t("scanSummary.unitPrice", { price: price(scanUnitAmount(selected)) })})</>
                )}
              </Text>

              {/* 수량 (FN-027) */}
              <HStack gap={2} align="center">
                <Text as="span" type="supporting">{t("scanSummary.qty")}</Text>
                <button
                  type="button"
                  className={styles.qtyButton}
                  disabled={selected.qty <= 1}
                  onClick={() => updateItem(selected.id, { qty: selected.qty - 1 })}
                  aria-label={t("scanSummary.qtyMinus")}
                >
                  −
                </button>
                <span className={styles.qtyValue}>{selected.qty}</span>
                <button
                  type="button"
                  className={styles.qtyButton}
                  /* 999 에서 '+' 가 죽는다 (FN-027, design 2026-08-28). */
                  disabled={selected.qty >= QTY_MAX}
                  onClick={() => updateItem(selected.id, { qty: clampQty(selected.qty + 1) })}
                  aria-label={t("scanSummary.qtyPlus")}
                >
                  +
                </button>
              </HStack>

              {/* 유형(FINISH) — 배리언트 시세가 있는 카드만 */}
              {finishOptions.length > 0 && (
                <VStack gap={1}>
                  <Text as="span" type="supporting">{t("scanSummary.finish")}</Text>
                  <HStack gap={1} wrap="wrap">
                    <ToggleButton
                      size="sm"
                      data-compact=""
                      label={t("scanSummary.finishDefault")}
                      isPressed={selected.finish === null}
                      onPressedChange={() =>
                        updateItem(selected.id, { finish: null, unitOverride: null })
                      }
                    />
                    {finishOptions.map((o) => (
                      <ToggleButton
                        key={o.finish}
                        size="sm"
                        data-compact=""
                        label={o.finish}
                        isPressed={selected.finish === o.finish}
                        onPressedChange={() =>
                          updateItem(selected.id, {
                            finish: o.finish,
                            unitOverride: o.amount,
                          })
                        }
                      />
                    ))}
                  </HStack>
                </VStack>
              )}

              {/* 등급(Grading) — 시세 미적재라 자리만 (§5.4 준비 중) */}
              <Text as="p" type="supporting" color="secondary">
                {t("scanSummary.grade")} · {t("scanSummary.gradePending")}
              </Text>

              {/* 잘못 인식됐어요 (SC-027 · FN-074) — **담기 전**이 신고하기 제일 좋은
                  자리다. 여기서는 무엇이 잘못 잡혔는지 눈앞에 있고, 담고 나면 그 카드가
                  보유에 섞여 어느 것이 오인식이었는지 흐려진다.
                  ⚠ 아이콘 색은 액센트다 — 회색이면 옆의 안내 글과 한 덩어리로 읽힌다. */}
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="Report" size="sm" color="var(--color-icon-accent)" />}
                label={t("report.open")}
                clickAction={() => openReport(selected.card.cardUid)}
              />
            </VStack>
          </div>
        )}
      </div>

      {notice && <p className={styles.notice}>{notice}</p>}

      {/* 하단 — 컬렉션에 담기 (전폭). 비회원은 로그인 오버레이부터. */}
      <div className={styles.saveBar}>
        <Button
          variant="primary"
          label={t("scanSummary.addToCollection", { count: totals.cardCount })}
          isDisabled={saving}
          clickAction={() => {
            if (!isAuthenticated) {
              requireAuth({ type: "none" });
              return;
            }
            setSaveOpen(true);
          }}
        />
      </div>

      {reportingCard && (
        <MisreadReportSheet
          open
          onClose={closeReport}
          card={{
            cardUid: reportingCard.cardUid,
            cardSerialNo: reportingCard.cardSerialNo,
            name: reportingCard.name,
            number: reportingCard.number,
            cardsetCode: reportingCard.cardsetCode,
            imagePathway: reportingCard.imagePathway,
          }}
        />
      )}

      <ConfirmDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={t("scanSummary.saveTitle")}
        description={`${t("scanSummary.saveBody", { count: totals.cardCount })} ${t("scanSummary.savePurchaseNote")}`}
        secondaryText={t("action.cancel", { ns: "common" })}
        primaryText={t("scanSummary.saveConfirm")}
        onSecondaryPressed={() => setSaveOpen(false)}
        onPrimaryPressed={() => void handleSave()}
      />
    </div>
  );
}
